import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useUser } from '../../context/UserContext';
import { recapturaResumen } from '../../api/apiConfig';
import { CATEGORIAS, fmtMM, pillClass, unwrap } from './recapturaUtils';

const RecapturaResumen = () => {
  const { user } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = unwrap(await recapturaResumen(user));
        if (cancelled) return;
        if (resp.ok === false) throw new Error(resp.error || 'Error');
        setData(resp);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudo cargar el resumen');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <AiOutlineLoading3Quarters className="animate-spin" /> Cargando resumen…
      </div>
    );
  }

  if (error) {
    return <div className="p-6"><div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div></div>;
  }

  const kpis = data?.kpis || {};
  const porCat = data?.por_categoria || {};
  const flujo = data?.flujo || {};
  const tot = data?.totales_cesiones || {};
  const maxExt = Math.max(1, ...CATEGORIAS.map((c) => Number(porCat[c]?.ext || 0)));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Activación Comercial — Resumen gerencial</h1>
        <p className="text-sm text-gray-600">
          {data?.campana ? `${data.campana.nombre} · ${data.campana.fec_ini} → ${data.campana.fec_fin}` : 'Sin campaña activa'}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ['Clientes en campaña', kpis.clientes, '3 categorías'],
          ['Cesiones externas (mes)', fmtMM(kpis.cesiones_externas), 'Bancos + otros factoring'],
          ['Propuestas aprobadas', kpis.propuestas_aprobadas, 'Incluye casos aún sin operar'],
          ['Cursado', kpis.cursado, 'Por cesión IF registrada'],
        ].map(([t, v, s]) => (
          <div key={t} className="seccion-datos">
            <div className="text-xs text-gray-500">{t}</div>
            <div className="text-3xl font-bold my-1">{v ?? 0}</div>
            <div className="text-xs text-gray-500">{s}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="seccion-datos">
          <h2 className="font-semibold text-gray-800 mb-3">Actividad de cartera por segmento</h2>
          {CATEGORIAS.map((cat) => {
            const ext = Number(porCat[cat]?.ext || 0);
            const pct = (ext / maxExt) * 100;
            return (
              <div key={cat} className="grid grid-cols-[140px_1fr_90px] items-center gap-2 mb-3 text-xs">
                <span>{cat}</span>
                <div className="h-4 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-sky-700" style={{ width: `${pct}%` }} />
                </div>
                <b>{fmtMM(ext)}</b>
              </div>
            );
          })}
          <hr className="my-4 border-slate-200" />
          <h2 className="font-semibold text-gray-800 mb-2">Cesiones de la muestra</h2>
          <div className="flex h-7 rounded overflow-hidden text-[11px] text-white">
            <div className="bg-sky-700 flex items-center justify-center" style={{ flex: Math.max(tot.if || 0, 0.01) }}>IF</div>
            <div className="bg-amber-500 flex items-center justify-center" style={{ flex: Math.max(tot.banco || 0, 0.01) }}>Bancos</div>
            <div className="bg-slate-500 flex items-center justify-center" style={{ flex: Math.max(tot.otros || 0, 0.01) }}>Otros</div>
          </div>
        </div>

        <div className="seccion-datos">
          <h2 className="font-semibold text-gray-800 mb-3">Flujo comercial</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              ['Pendiente / diagnóstico', flujo.pendiente_diagnostico],
              ['Gerencia Comercial /Riesgo', flujo.gerencia_riesgo],
              ['Compromiso', flujo.compromiso],
              ['Operó', flujo.cursado],
              ['Rechazado', flujo.rechazado_exclusion],
            ].map(([label, n]) => (
              <div key={label} className="bg-sky-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{n || 0}</div>
                <div className="text-[11px] text-gray-600">{label}</div>
              </div>
            ))}
          </div>
          <hr className="my-4 border-slate-200" />
          <h2 className="font-semibold text-gray-800 mb-2">Decisiones que requieren atención</h2>
          <div className="space-y-2 max-h-56 overflow-auto">
            {(data?.alertas || []).length === 0 && <p className="text-sm text-gray-500">Sin alertas.</p>}
            {(data?.alertas || []).map((a) => (
              <div key={a.id} className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/recaptura/ficha/${encodeURIComponent(a.rut)}`}
                  state={{ clienteId: a.id }}
                  className="text-sm border rounded-lg px-2 py-1 bg-white"
                  style={{ textDecoration: 'none' }}
                >
                  {a.cliente || a.rut}
                </Link>
                <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${pillClass(a.etapa)}`}>
                  {a.etapa}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="seccion-datos">
          <h2 className="font-semibold text-gray-800 mb-3">Últimos cierres de gestión</h2>
          <div className="border-l-2 border-slate-200 pl-4 space-y-3">
            {(data?.ultimos_cierres || []).map((e, i) => (
              <div key={i}>
                <div className="font-semibold text-sm">{e.fecha} · {e.cliente || e.rut}</div>
                <div className="text-xs text-gray-500">{e.actor} · {e.tipo}</div>
                <p className="text-sm">{e.detalle}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="seccion-datos">
          <h2 className="font-semibold text-gray-800 mb-3">Accesos rápidos</h2>
          <div className="flex flex-wrap gap-2">
            <Link to="/recaptura/matriz" className="btn-primary text-sm py-2 px-3" style={{ textDecoration: 'none' }}>Matriz comercial</Link>
            <Link to="/recaptura/aprobaciones" className="text-sm py-2 px-3 border rounded-lg bg-white" style={{ textDecoration: 'none' }}>Resolver propuestas</Link>
            <Link to="/recaptura/bitacora" className="text-sm py-2 px-3 border rounded-lg bg-white" style={{ textDecoration: 'none' }}>Bitácora</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecapturaResumen;
